import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { modal } from '@client/components/_ui/feedback/modal';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import {
    DEFAULT_ACTION_COLUMN,
    downloadQuestionnaireExport,
    questionnaireRecordToCreateInput,
    resolveImportName,
} from '@client/utils';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { ClipboardList, Copy, FileUp, Plus } from 'lucide-react';
import { useState } from 'react';
import { ImportQuestionnaireJsonModal } from './import-questionnaire-json/ImportQuestionnaireJsonModal';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/questionnaire')({
    staticData: {
        metadata: {
            title: 'Questionnaire',
        },
        ui: {
            icon: ClipboardList,
            title: 'Questionnaire configuration',
            subtitle: 'Build and manage claim questionnaires',
            menuDescription: 'Build and manage claim questionnaires',
        },
        presentation: 'fullPage',
        modules: ['claim'],
        permissions: ['settings:update'],
    },
    component: ConfigurationQuestionnaire,
});

type QueryParams = RouterInputs['questionnaire']['listQuestionnaires'];
type QuestionnaireRow = RouterOutputs['questionnaire']['listQuestionnaires']['rows'][number];
type QuestionnaireOrderBy = NonNullable<QueryParams['orderBy']>;

const columnHelper = createColumnHelper<QuestionnaireRow, QuestionnaireOrderBy>();

function ConfigurationQuestionnaire() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { trpcClient } = useTrpc();
    const { can } = usePermissions();
    const canAdminOrg = can('org:admin');
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [queryParams, setQueryParams] = useState<RouterInputs['questionnaire']['listQuestionnaires']>({
        limit: 10,
        offset: 0,
        orderBy: [],
    });
    const { data, isLoading, refetch } = trpc.questionnaire.listQuestionnaires.useQuery(queryParams);
    const { mutateAsync: deleteQuestionnaire } = trpc.questionnaire.deleteQuestionnaire.useMutation();
    const { mutateAsync: createQuestionnaire } = trpc.questionnaire.createQuestionnaire.useMutation();

    const duplicateQuestionnaire = (row: QuestionnaireRow) => {
        modal.confirm({
            title: `Duplicate "${row.name}"?`,
            content: 'A copy of this questionnaire will be created and opened in the editor.',
            okText: 'Duplicate',
            async onOk() {
                try {
                    const [record, options] = await Promise.all([
                        trpcClient.questionnaire.getQuestionnaire.query({ id: row.id }),
                        trpcClient.questionnaire.listQuestionnaireOptions.query({}),
                    ]);
                    const name = resolveImportName(record.name, new Set(options.map((o) => o.name)), 'copy');
                    const created = await createQuestionnaire({
                        name,
                        ...questionnaireRecordToCreateInput(record),
                    });
                    message.success('Questionnaire duplicated.');
                    void refetch();
                    navigate({
                        to: '/console/$orgId/configuration/claims-products/questionnaire/edit/$questionnaireId',
                        params: { orgId, questionnaireId: created.questionnaire.id },
                    });
                } catch (e) {
                    errorMessage.show(e);
                }
            },
        });
    };

    const columns = [
        columnHelper.accessor('id', {
            id: 'id',
            header: 'ID',
            sortable: 'id',
            cell: ({ value }) => String(value),
            fixed: 'left',
            align: 'center',
            width: 100,
        }),
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Name',
            sortable: 'name',
        }),
        columnHelper.actions({
            id: 'actions',
            cell: ({ row }) => (
                <div onClick={(e) => e.stopPropagation()}>
                    <Space size="small">
                        <Button.Edit.Link
                            to="/console/$orgId/configuration/claims-products/questionnaire/edit/$questionnaireId"
                            params={{ orgId, questionnaireId: row.id }}
                        />
                        <Button.Icon
                            icon={<Copy />}
                            size="small"
                            tooltip="Duplicate"
                            onClick={() => duplicateQuestionnaire(row)}
                        />
                        <Button.Download
                            tooltip="Export"
                            onClick={async () => {
                                try {
                                    // The list query only selects id/name — fetch the full record first.
                                    const record = await trpcClient.questionnaire.getQuestionnaire.query({
                                        id: row.id,
                                    });
                                    downloadQuestionnaireExport(record);
                                } catch (e) {
                                    errorMessage.show(e);
                                }
                            }}
                        />
                        {canAdminOrg && (
                            <Button.Delete
                                onConfirm={async (callback) => {
                                    try {
                                        await deleteQuestionnaire({ id: row.id });
                                        callback();
                                        void refetch();
                                    } catch (e) {
                                        errorMessage.show(e);
                                    }
                                }}
                            />
                        )}
                    </Space>
                </div>
            ),
            ...DEFAULT_ACTION_COLUMN,
        }),
    ];

    const tableProps = usePrismaTable<QuestionnaireRow, QueryParams['where'], QuestionnaireOrderBy>({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'both',
        },
    });

    return (
        <PageController route={Route}>
            <Outlet />

            <ImportQuestionnaireJsonModal
                open={isImportOpen}
                onClose={(createdId) => {
                    setIsImportOpen(false);
                    if (createdId !== undefined) {
                        void refetch();
                        navigate({
                            to: '/console/$orgId/configuration/claims-products/questionnaire/edit/$questionnaireId',
                            params: { orgId, questionnaireId: createdId },
                        });
                    }
                }}
            />

            <TableToolbar
                filters={undefined}
                actions={
                    <Space>
                        <Button type="secondary" icon={<FileUp />} onClick={() => setIsImportOpen(true)}>
                            Import questionnaire
                        </Button>
                        <Button
                            type="primary"
                            icon={<Plus />}
                            onClick={() =>
                                navigate({
                                    to: '/console/$orgId/configuration/claims-products/questionnaire/add',
                                    params: { orgId },
                                })
                            }
                        >
                            Add new
                        </Button>
                    </Space>
                }
            />

            <PrismaTable
                {...tableProps}
                size="small"
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick: (e) => {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/claims-products/questionnaire/edit/$questionnaireId',
                                params: { orgId, questionnaireId: id },
                            },
                            e,
                        );
                    },
                })}
            />
        </PageController>
    );
}
